import { MedicalChronologyConstants } from "@/components/MedicalChronology/constants";
import { LabeledSwitch } from "@/components/ui/LabeledSwitch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useElementWidth } from "@/hooks/useElementWidth";
import {
  ArrowDownWideNarrow,
  ChevronDown,
  Filter,
  ListChecks,
  Rows3,
  Search,
  SlidersHorizontal,
  Table2,
  X,
} from "lucide-react";
import React, { useEffect } from "react";

import { VisitBulkSelectCheckbox } from "./VisitBulkSelectCheckbox";
import { VisitsToolbarFilterGroupView } from "./VisitsToolbarFilterGroupView";
import { VisitsToolbarNativeSelect } from "./VisitsToolbarNativeSelect";
import {
  ChronologySortField,
  ChronologyViewType,
  type EditingVisitDetailState,
  SortDirection,
  type VisitTypeFilterOption,
} from "./types";
import { hideVisitTypes, showVisitTypes, toggleVisitTypeHidden } from "./utils/visitTypeFilter";

/**
 * Layout tiers, chosen from the toolbar's own container width (not the viewport)
 * so it reflows correctly when the file side panel narrows the visits column:
 *  - "wide"   (>= WIDE): everything on one row
 *  - "medium" (>= MEDIUM): two rows — [search · filters] / [toggles]
 *  - "narrow" (< MEDIUM): fully stacked (phone)
 */
const WIDE_MIN_WIDTH = 920;
const MEDIUM_MIN_WIDTH = 640;

type ToolbarSize = "narrow" | "medium" | "wide";

const getToolbarSize = (width: number): ToolbarSize => {
  if (width >= WIDE_MIN_WIDTH) return "wide";
  if (width >= MEDIUM_MIN_WIDTH) return "medium";
  return "narrow";
};

export interface VisitsChronologyToolbarProps {
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  viewType: ChronologyViewType;
  setViewType: (v: ChronologyViewType) => void;
  chronologySortField: ChronologySortField;
  chronologySortDirection: SortDirection;
  setChronologySort: (field: ChronologySortField, direction: SortDirection) => void;
  visitTypeFilterOptions: VisitTypeFilterOption[];
  hiddenVisitTypeIds: string[];
  setHiddenVisitTypeIds: (ids: string[]) => void;
  collapseTherapyVisits: boolean;
  setCollapseTherapyVisits: (v: boolean) => void;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  /** False hides the Edit Mode switch, e.g. for an analyst on a finished case. */
  isEditModeAvailable: boolean;
  onCancelEditHeader: () => void;
  setEditingVisitDetail: (v: EditingVisitDetailState | null) => void;
  setShowNewDetailForm: (v: string | null) => void;
  visitsDisplayMode: "list" | "table";
  setVisitsDisplayMode: (mode: "list" | "table") => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
}

export const VisitsChronologyToolbar: React.FC<VisitsChronologyToolbarProps> = ({
  searchQuery,
  setSearchQuery,
  viewType,
  setViewType,
  chronologySortField,
  chronologySortDirection,
  setChronologySort,
  visitTypeFilterOptions,
  hiddenVisitTypeIds,
  setHiddenVisitTypeIds,
  collapseTherapyVisits,
  setCollapseTherapyVisits,
  editMode,
  setEditMode,
  isEditModeAvailable,
  onCancelEditHeader,
  setEditingVisitDetail,
  setShowNewDetailForm,
  visitsDisplayMode,
  setVisitsDisplayMode,
  searchInputRef,
}) => {
  const [containerRef, width] = useElementWidth<HTMLDivElement>();
  const size = getToolbarSize(width);
  const hiddenVisitTypeIdSet = new Set(hiddenVisitTypeIds);
  const hiddenVisitTypeOptionCount = visitTypeFilterOptions.filter((option) =>
    hiddenVisitTypeIdSet.has(option.visitTypeId)
  ).length;
  const isEveryVisitTypeShown = hiddenVisitTypeOptionCount === 0;
  const hasActiveFilter =
    viewType !== ChronologyViewType.DATE || collapseTherapyVisits || !isEveryVisitTypeShown;

  // Close "More filters" if the container widens past narrow, since its trigger disappears then.
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = React.useState(false);
  useEffect(() => {
    if (size !== "narrow") setIsMoreFiltersOpen(false);
  }, [size]);

  const renderVisitTypeFilter = (variant: "popover" | "inline") => {
    const visitTypeFilterLabel = isEveryVisitTypeShown
      ? "All Types"
      : `${hiddenVisitTypeOptionCount} hidden`;
    return (
      <VisitsToolbarFilterGroupView
        className={
          variant === "popover"
            ? "w-full"
            : "min-w-0 flex-1 group-data-[size=medium]/medtb:min-w-0 group-data-[size=wide]/medtb:min-w-0 group-data-[size=medium]/medtb:flex-[0_1_auto] group-data-[size=wide]/medtb:flex-[0_1_auto]"
        }
        icon={
          <ListChecks
            size={14}
            strokeWidth={2.5}
            className={variant === "inline" ? "hidden sm:block" : undefined}
          />
        }
        label={
          <span className="whitespace-nowrap text-2xs font-semibold uppercase tracking-micro">
            Type
          </span>
        }
      >
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Filter visits by type: ${visitTypeFilterLabel}`}
              className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-1 py-1 text-xs font-bold text-neutral-800 focus-visible:outline-none"
            >
              <span className="truncate">{visitTypeFilterLabel}</span>
              <ChevronDown size={14} strokeWidth={2.5} className="shrink-0 text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="flex max-h-80 w-64 flex-col gap-0.5 overflow-y-auto p-1.5"
          >
            {visitTypeFilterOptions.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-neutral-400">No visit types</p>
            ) : (
              <>
                <label className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-100">
                  <VisitBulkSelectCheckbox
                    ariaLabel="All visit types"
                    checked={isEveryVisitTypeShown}
                    indeterminate={
                      !isEveryVisitTypeShown &&
                      hiddenVisitTypeOptionCount < visitTypeFilterOptions.length
                    }
                    onToggle={() => {
                      const listedVisitTypeIds = visitTypeFilterOptions.map(
                        (option) => option.visitTypeId
                      );
                      setHiddenVisitTypeIds(
                        isEveryVisitTypeShown
                          ? hideVisitTypes(hiddenVisitTypeIds, listedVisitTypeIds)
                          : showVisitTypes(hiddenVisitTypeIds, listedVisitTypeIds)
                      );
                    }}
                  />
                  All
                </label>
                <div className="my-1 h-px w-full shrink-0 bg-neutral-200" />
                {visitTypeFilterOptions.map((option) => (
                  <label
                    key={option.visitTypeId}
                    className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
                  >
                    <VisitBulkSelectCheckbox
                      ariaLabel={option.label}
                      checked={!hiddenVisitTypeIdSet.has(option.visitTypeId)}
                      indeterminate={false}
                      onToggle={() =>
                        setHiddenVisitTypeIds(
                          toggleVisitTypeHidden(hiddenVisitTypeIds, option.visitTypeId)
                        )
                      }
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}{" "}
                      <span className="tabular-nums text-neutral-400">({option.visitCount})</span>
                    </span>
                  </label>
                ))}
              </>
            )}
          </PopoverContent>
        </Popover>
      </VisitsToolbarFilterGroupView>
    );
  };

  /**
   * Rendered in both the narrow "More filters" popover and the inline medium/wide row,
   * so the mutual-exclusion rules below live in one place instead of two copies drifting apart.
   */
  const renderGroupFilter = (variant: "popover" | "inline") => (
    <VisitsToolbarFilterGroupView
      className={
        variant === "popover"
          ? "w-full"
          : "min-w-0 flex-1 group-data-[size=medium]/medtb:min-w-0 group-data-[size=wide]/medtb:min-w-0 group-data-[size=medium]/medtb:flex-[0_1_auto] group-data-[size=wide]/medtb:flex-[0_1_auto]"
      }
      icon={
        <Filter
          size={14}
          strokeWidth={2.5}
          className={variant === "inline" ? "hidden sm:block" : undefined}
        />
      }
      label={
        <span className="whitespace-nowrap text-2xs font-semibold uppercase tracking-micro">
          Group
        </span>
      }
    >
      <VisitsToolbarNativeSelect
        aria-label="Group visits by"
        value={viewType}
        onChange={(e) => setViewType(e.target.value as ChronologyViewType)}
      >
        <option value={ChronologyViewType.DATE}>Chronology</option>
        <option value={ChronologyViewType.PROVIDER}>Provider</option>
        <option value={ChronologyViewType.VISIT_TYPE}>Type</option>
        <option value={ChronologyViewType.FILE}>Files</option>
      </VisitsToolbarNativeSelect>
    </VisitsToolbarFilterGroupView>
  );

  const renderSortFilter = (variant: "popover" | "inline") => (
    <VisitsToolbarFilterGroupView
      className={
        variant === "popover"
          ? "w-full"
          : "min-w-0 flex-1 group-data-[size=medium]/medtb:min-w-0 group-data-[size=wide]/medtb:min-w-0 group-data-[size=medium]/medtb:flex-[0_1_auto] group-data-[size=wide]/medtb:flex-[0_1_auto]"
      }
      icon={
        <ArrowDownWideNarrow
          size={14}
          strokeWidth={2.5}
          className={variant === "inline" ? "hidden sm:block" : undefined}
        />
      }
      label={
        <span className="whitespace-nowrap text-2xs font-semibold uppercase tracking-micro">
          Sort
        </span>
      }
    >
      <VisitsToolbarNativeSelect
        id={
          variant === "popover"
            ? "visits-sort-date-direction-popover"
            : "visits-sort-date-direction"
        }
        aria-label="Sort visits by"
        value={`${chronologySortField}-${chronologySortDirection}`}
        onChange={(e) => {
          const [field, direction] = e.target.value.split("-") as [
            ChronologySortField,
            SortDirection,
          ];
          setChronologySort(field, direction);
        }}
      >
        <option value={`${ChronologySortField.DATE}-${SortDirection.ASC}`}>Oldest First</option>
        <option value={`${ChronologySortField.DATE}-${SortDirection.DESC}`}>Newest First</option>
        {viewType === ChronologyViewType.FILE && (
          <>
            <option value={`${ChronologySortField.PAGE}-${SortDirection.ASC}`}>
              Page Ascending
            </option>
            <option value={`${ChronologySortField.PAGE}-${SortDirection.DESC}`}>
              Page Descending
            </option>
          </>
        )}
        {viewType !== ChronologyViewType.DATE && (
          <>
            <option value={`${ChronologySortField.NAME}-${SortDirection.ASC}`}>Group A–Z</option>
            <option value={`${ChronologySortField.NAME}-${SortDirection.DESC}`}>Group Z–A</option>
          </>
        )}
      </VisitsToolbarNativeSelect>
    </VisitsToolbarFilterGroupView>
  );

  const renderCollapseTherapySwitch = () => (
    <LabeledSwitch
      aria-label="Collapse therapy visits"
      checked={collapseTherapyVisits}
      onCheckedChange={() => {
        setCollapseTherapyVisits(!collapseTherapyVisits);
        if (collapseTherapyVisits && editMode) setEditMode(false);
      }}
      label="Collapse Therapy"
      variant="blue"
    />
  );

  const renderDisplayModeToggle = () => (
    <div className="flex h-10 shrink-0 items-center gap-0.5 rounded-xl border border-neutral-200/80 bg-neutral-50/50 p-1">
      <button
        type="button"
        aria-label="List view"
        title="List view"
        aria-pressed={visitsDisplayMode === "list"}
        onClick={() => setVisitsDisplayMode("list")}
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          visitsDisplayMode === "list"
            ? "bg-card text-primary shadow-sm"
            : "text-neutral-400 hover:text-neutral-600"
        }`}
      >
        <Rows3 size={14} strokeWidth={2.5} />
      </button>
      <button
        type="button"
        aria-label="Table view"
        title="Table view"
        aria-pressed={visitsDisplayMode === "table"}
        onClick={() => setVisitsDisplayMode("table")}
        className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${
          visitsDisplayMode === "table"
            ? "bg-card text-primary shadow-sm"
            : "text-neutral-400 hover:text-neutral-600"
        }`}
      >
        <Table2 size={14} strokeWidth={2.5} />
      </button>
    </div>
  );

  const renderEditModeSwitch = () => (
    <LabeledSwitch
      aria-label="Edit mode"
      checked={editMode}
      onCheckedChange={() => {
        if (editMode) {
          onCancelEditHeader();
          setEditingVisitDetail(null);
          setShowNewDetailForm(null);
        }
        setEditMode(!editMode);
        if (!editMode && collapseTherapyVisits) setCollapseTherapyVisits(false);
      }}
      label="Edit Mode"
      variant="red"
      disabled={!isEditModeAvailable}
      disabledReason={MedicalChronologyConstants.FINISHED_CASE_READ_ONLY_REASON}
    />
  );

  return (
    <div ref={containerRef} data-size={size} className="group/medtb overflow-hidden">
      <div className="flex flex-col gap-y-2 px-1 py-1 group-data-[size=wide]/medtb:flex-row group-data-[size=wide]/medtb:items-center group-data-[size=wide]/medtb:justify-between group-data-[size=wide]/medtb:gap-x-6">
        {/* Group 1: Search + Filters — one row from "medium" up */}
        <div className="flex min-w-0 flex-1 flex-col gap-2 group-data-[size=medium]/medtb:flex-row group-data-[size=wide]/medtb:flex-row group-data-[size=medium]/medtb:items-center group-data-[size=wide]/medtb:items-center">
          <div className="flex min-w-0 flex-1 items-center gap-2 group-data-[size=medium]/medtb:min-w-[160px] group-data-[size=wide]/medtb:min-w-[160px] group-data-[size=medium]/medtb:max-w-sm group-data-[size=wide]/medtb:max-w-sm">
            <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-xl border border-neutral-200/80 bg-neutral-50/50 px-3 py-1 transition-colors focus-within:shadow-focus-inset hover:bg-neutral-100/50 group-data-[size=medium]/medtb:min-w-[160px] group-data-[size=wide]/medtb:min-w-[160px] group-data-[size=medium]/medtb:max-w-sm group-data-[size=wide]/medtb:max-w-sm">
              <Search
                size={14}
                strokeWidth={2.5}
                className="shrink-0 text-neutral-400"
                aria-hidden
              />
              <input
                ref={searchInputRef}
                type="text"
                aria-label="Search visits"
                placeholder="Search visits…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-xs font-medium text-neutral-700 placeholder:text-neutral-400 focus:outline-none"
              />
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchQuery("")}
                className={`shrink-0 cursor-pointer text-neutral-400 transition-opacity hover:text-neutral-600 ${searchQuery ? "opacity-100" : "pointer-events-none opacity-0"}`}
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
            <div className="shrink-0 group-data-[size=medium]/medtb:hidden group-data-[size=wide]/medtb:hidden">
              <Popover open={isMoreFiltersOpen} onOpenChange={setIsMoreFiltersOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    aria-label="More filters"
                    className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neutral-200/80 bg-neutral-50/50 text-neutral-500 transition-colors hover:bg-neutral-100/50 focus-visible:shadow-focus-inset"
                  >
                    <SlidersHorizontal size={16} strokeWidth={2.5} />
                    {hasActiveFilter && (
                      <span
                        data-testid="more-filters-active-dot"
                        className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-primary"
                      />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="flex w-72 flex-col gap-3">
                  {renderVisitTypeFilter("popover")}
                  {renderGroupFilter("popover")}
                  {renderSortFilter("popover")}
                  <div className="h-px w-full bg-neutral-200" />
                  {renderDisplayModeToggle()}
                  {renderCollapseTherapySwitch()}
                  {renderEditModeSwitch()}
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <div className="hidden min-w-0 items-center gap-2 group-data-[size=medium]/medtb:flex group-data-[size=wide]/medtb:flex group-data-[size=medium]/medtb:w-auto group-data-[size=wide]/medtb:w-auto group-data-[size=medium]/medtb:shrink-0">
            {renderVisitTypeFilter("inline")}
            {renderGroupFilter("inline")}
            {renderSortFilter("inline")}
          </div>
        </div>

        {/* Group 2: Mode Controls — hidden at narrow (moved into the "More filters" popover above) */}
        <div className="hidden shrink-0 items-center gap-3 group-data-[size=medium]/medtb:flex group-data-[size=wide]/medtb:flex">
          {renderDisplayModeToggle()}
          <div className="h-6 w-px bg-neutral-200" />
          {renderCollapseTherapySwitch()}
          <div className="h-6 w-px bg-neutral-200" />
          {renderEditModeSwitch()}
        </div>
      </div>
    </div>
  );
};
